import React, { useState, useEffect } from 'react';
import { X, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { normalizeSourceName, parseSourceUrl, getFaviconUrl } from '../../utils/leadPanelUtils';
import { Badge } from '../ui/Badge';

interface SourceReference {
  url: string;
  title?: string;
  justification?: string;
}

interface ReasoningCriterion {
  criterion_text: string;
  reasoning_text: string;
  satisfied: 'yes' | 'no' | 'unclear';
  source_urls: (string | SourceReference | any)[];
}

interface CompanyReasoningModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  companyUrl?: string;
  summary?: string;
  reasoning: Record<string, ReasoningCriterion>;
  overallStatus?: 'match' | 'no-match' | 'partial';
  criteria?: string;
}

export const CompanyReasoningModal: React.FC<CompanyReasoningModalProps> = ({
  isOpen,
  onClose,
  companyName,
  summary,
  reasoning,
  overallStatus = 'match',
  criteria,
}) => {
  const [showMoreReferences, setShowMoreReferences] = useState<Record<string, boolean>>({});

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      // Store the current overflow value before locking
      const previousOverflow = document.body.style.overflow;

      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleEscape);
        // Restore the previous overflow value instead of hardcoding 'unset'
        document.body.style.overflow = previousOverflow;
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Map status to Badge variant
  const getBadgeVariant = (): 'success' | 'error' | 'warning' | 'neutral-gray' => {
    switch (overallStatus) {
      case 'match':
        return 'success';
      case 'no-match':
        return 'error';
      case 'partial':
        return 'warning';
      default:
        return 'neutral-gray';
    }
  };

  const getBadgeText = () => {
    switch (overallStatus) {
      case 'match':
        return 'Match';
      case 'no-match':
        return 'No Match';
      case 'partial':
        return 'Partial';
      default:
        return 'Unknown';
    }
  };
  const criteriaArray = Object.values(reasoning);
  const filteredCriteriaArray = criteriaArray.filter(criterion => !criteria || criterion.criterion_text === criteria);
  const totalReferences = filteredCriteriaArray.reduce((sum, c) => sum + (c.source_urls?.length || 0), 0);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        backgroundColor: 'rgba(17, 24, 39, 0.4)',
        backdropFilter: 'blur(8px)',
        animation: 'fadeIn 150ms ease-out'
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
      `}</style>

      <div
        className="bg-white shadow-lg overflow-hidden flex flex-col font-sans"
        style={{
          width: '680px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          animation: 'scaleIn 200ms ease-out',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '28px 24px 24px 24px', borderBottom: '1px solid #E5E7EB' }}>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0" style={{ paddingRight: '32px' }}>
              <div className="flex items-center" style={{ gap: '8px', marginBottom: '8px' }}>
                <h2
                  id="modal-title"
                  style={{
                    fontSize: '22px',
                    fontWeight: 600,
                    color: '#111827',
                    margin: 0,
                    lineHeight: 1.2,
                    fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                  }}
                >
                  {companyName}
                </h2>
                <Badge variant={getBadgeVariant()} size="md" shape="pill" className="text-13 font-bold py-1">
                  {getBadgeText()}
                </Badge>
              </div>

              {summary && (
                <p
                  style={{
                    fontSize: '15px',
                    color: '#374151',
                    margin: 0,
                    lineHeight: 1.5,
                    maxWidth: '90%',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    marginBottom: '16px',
                    fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                  }}
                >
                  {summary.endsWith('.') || summary.endsWith('!') || summary.endsWith('?') ? summary : summary}
                </p>
              )}
            </div>

            <button
              id="modal-close-btn"
              onClick={onClose}
              className="flex-shrink-0 transition-colors"
              style={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6B7280',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                borderRadius: '6px',
                position: 'absolute',
                right: '24px',
                top: '24px',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#111827';
                e.currentTarget.style.backgroundColor = '#F3F4F6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6B7280';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              aria-label="Close dialog"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '28px 24px'
          }}
        >
          {/* Reasoning Section */}
          {filteredCriteriaArray.length > 0 && (
            <>
              <div className='flex flex-col gap-8'>
                {filteredCriteriaArray.map((criterion, index) => (
                  <div key={index}>
                    <h3
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '5px',
                        marginTop: 0,
                        fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                      }}
                    >
                      Criteria
                    </h3>
                    <p
                      style={{
                        fontSize: '15px',
                        color: '#111827',
                        lineHeight: 1.6,
                        margin: 0,
                        marginBottom: '10px',
                        fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                      }}
                    >
                      {criterion.criterion_text}
                    </p>
                    <h3
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '5px',
                        marginTop: 0,
                        fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                      }}
                    >
                      Reasoning
                    </h3>
                    <p
                      style={{
                        fontSize: '15px',
                        color: '#111827',
                        lineHeight: 1.6,
                        margin: 0,
                        marginBottom: index < filteredCriteriaArray.length - 1 ? '12px' : 0,
                        fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                      }}
                    >
                      {criterion.reasoning_text}
                    </p>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '20px 0' }} />
            </>
          )}

          {/* References Section */}
          {totalReferences > 0 && (
            <>
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '16px',
                  marginTop: 0,
                  paddingTop: '16px',
                  fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                }}
              >
                References ({totalReferences})
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {filteredCriteriaArray.map((criterion, criterionIdx) => {
                  if (!criterion.source_urls || criterion.source_urls.length === 0) return null;

                  const sources = criterion.source_urls
                    .map(parseSourceUrl)
                    .filter((s) => s !== null)
                    .reverse();

                  const visibleCount = showMoreReferences[criterionIdx] ? sources.length : Math.min(6, sources.length);
                  const hasMore = sources.length > 6;

                  return (
                    <div key={criterionIdx}>
                      {sources.slice(0, visibleCount).map((source, sourceIdx) => {
                        if (!source) return null;
                        const displayName = source.title || normalizeSourceName(source.hostname);
                        const favicon = getFaviconUrl(source.hostname, 64);

                        // Clean URL - remove query params for display
                        const cleanUrl = source.url.split('?')[0];

                        return (
                          <div
                            key={sourceIdx}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                              marginBottom: sourceIdx < visibleCount - 1 ? '16px' : 0
                            }}
                          >
                            {/* Row 1: Source name with icon */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <img
                                src={favicon}
                                alt=""
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  flexShrink: 0,
                                  borderRadius: '3px'
                                }}
                                onError={(e) => {
                                  const target = e.currentTarget;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    const placeholder = document.createElement('div');
                                    placeholder.style.width = '18px';
                                    placeholder.style.height = '18px';
                                    placeholder.style.borderRadius = '50%';
                                    placeholder.style.backgroundColor = '#E5E7EB';
                                    placeholder.style.display = 'flex';
                                    placeholder.style.alignItems = 'center';
                                    placeholder.style.justifyContent = 'center';
                                    placeholder.style.fontSize = '11px';
                                    placeholder.style.fontWeight = '600';
                                    placeholder.style.color = '#6B7280';
                                    placeholder.textContent = source.hostname.charAt(0).toUpperCase();
                                    parent.insertBefore(placeholder, target);
                                  }
                                }}
                              />
                              <span
                                style={{
                                  fontSize: '15px',
                                  fontWeight: 600,
                                  color: '#111827',
                                  fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                                }}
                              >
                                {displayName}
                              </span>
                            </div>

                            {/* Row 2: URL with external link */}
                            <a
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: '14px',
                                color: '#2563EB',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                wordBreak: 'break-all',
                                transition: 'text-decoration 0.15s ease',
                                fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                            >
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {cleanUrl}
                              </span>
                              <ExternalLink
                                size={14}
                                style={{ flexShrink: 0, color: '#2563EB' }}
                                aria-hidden="true"
                              />
                            </a>

                            {/* Row 3: Snippet */}
                            <p
                              style={{
                                fontSize: '14px',
                                color: '#374151',
                                margin: 0,
                                marginTop: '4px',
                                lineHeight: 1.4,
                                fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                              }}
                            >
                              {source.snippet || source.justification || criterion.criterion_text || "No excerpt available"}
                            </p>
                          </div>
                        );
                      })}

                      {/* Show more button */}
                      {hasMore && (
                        <button
                          id="show-more-references-btn"
                          onClick={() => setShowMoreReferences(prev => ({
                            ...prev,
                            [criterionIdx]: !prev[criterionIdx]
                          }))}
                          style={{
                            marginTop: '12px',
                            padding: 0,
                            border: 'none',
                            background: 'none',
                            color: '#2563EB',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'text-decoration 0.15s ease',
                            fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                        >
                          {showMoreReferences[criterionIdx] ? (
                            <>
                              Show less <ChevronUp size={14} />
                            </>
                          ) : (
                            <>
                              Show more (+{sources.length - 6}) <ChevronDown size={14} />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
